<template>
    <v-container fluid class="registrazione-container fill-height">
        <v-row justify="center" align="center" class="py-8">
            <v-col cols="12" sm="10" md="7" lg="5">

                <!-- Step 1: Form registrazione -->
                <template v-if="step === 1">
                    <v-card class="reg-card elevation-8">
                        <v-card-text class="pa-6">

                            <div class="text-center mb-6 d-flex flex-column align-center">
                                <v-img :src="require('@/assets/logo.png')" width="64" height="67" />
                                <div class="text-h5 font-weight-bold mt-2">Registra la tua azienda</div>
                                <div class="text-caption text-medium-emphasis">
                                    Crea un account per accedere al gestionale rilievi misure
                                </div>
                            </div>

                            <v-form ref="formRef" @submit.prevent="invia">

                                <!-- Dati azienda -->
                                <div class="section-label mb-2">
                                    <v-icon size="15" class="me-1">mdi-office-building-outline</v-icon>
                                    Dati azienda
                                </div>

                                <v-text-field v-model="form.ragioneSociale" label="Ragione Sociale" variant="outlined"
                                    density="comfortable" prepend-inner-icon="mdi-domain" :rules="[rules.required]"
                                    class="mb-1" />

                                <v-text-field v-model="form.pIva" label="Partita IVA" variant="outlined"
                                    density="comfortable" prepend-inner-icon="mdi-card-text-outline"
                                    :rules="[rules.required, rules.pIva]" class="mb-1" maxlength="11" counter />

                                <v-divider class="my-4" />

                                <!-- Dati accesso -->
                                <div class="section-label mb-2">
                                    <v-icon size="15" class="me-1">mdi-account-key-outline</v-icon>
                                    Dati di accesso
                                </div>

                                <v-text-field v-model="form.email" label="Email (sarà il tuo username)"
                                    variant="outlined" density="comfortable" type="email"
                                    prepend-inner-icon="mdi-email-outline" :rules="[rules.required, rules.email]"
                                    autocomplete="email" class="mb-1" />

                                <v-text-field v-model="form.password" label="Password" variant="outlined"
                                    density="comfortable" :type="showPass ? 'text' : 'password'"
                                    prepend-inner-icon="mdi-lock-outline"
                                    :append-inner-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
                                    @click:append-inner="showPass = !showPass" :rules="[rules.required, rules.password]"
                                    class="mb-1" />

                                <v-text-field v-model="form.confermaPassword" label="Conferma Password"
                                    variant="outlined" density="comfortable" :type="showPass ? 'text' : 'password'"
                                    prepend-inner-icon="mdi-lock-check-outline"
                                    :rules="[rules.required, rules.confermaPassword]" class="mb-1" />

                                <!-- Requisiti password -->
                                <v-expand-transition>
                                    <div v-if="form.password" class="password-hints mb-3">
                                        <div class="hint-item" :class="{ ok: form.password.length >= 8 }">
                                            <v-icon size="13">{{ form.password.length >= 8 ? 'mdi-check' :
                                                'mdi-circle-small' }}</v-icon>
                                            Almeno 8 caratteri
                                        </div>
                                        <div class="hint-item" :class="{ ok: /[A-Z]/.test(form.password) }">
                                            <v-icon size="13">{{ /[A-Z]/.test(form.password) ? 'mdi-check' :
                                                'mdi-circle-small' }}</v-icon>
                                            Una lettera maiuscola
                                        </div>
                                        <div class="hint-item" :class="{ ok: /[^a-zA-Z0-9]/.test(form.password) }">
                                            <v-icon size="13">{{ /[^a-zA-Z0-9]/.test(form.password) ? 'mdi-check' :
                                                'mdi-circle-small' }}</v-icon>
                                            Un carattere speciale (!@#$…)
                                        </div>
                                    </div>
                                </v-expand-transition>

                                <v-btn block size="large" color="primary" class="mt-2" type="submit" :loading="loading"
                                    :disabled="loading">
                                    Registrati
                                </v-btn>

                            </v-form>

                            <div class="text-center mt-4">
                                <span class="text-caption text-medium-emphasis">Hai già un account? </span>
                                <v-btn variant="text" size="small" color="primary" :to="{ path: '/login' }">
                                    Accedi
                                </v-btn>
                            </div>

                        </v-card-text>
                    </v-card>
                </template>

                <!-- Step 2: Conferma avvenuta -->
                <template v-if="step === 2">
                    <v-card class="reg-card elevation-8">
                        <v-card-text class="pa-8 text-center">
                            <v-icon icon="mdi-email-fast-outline" color="primary" size="64" class="mb-4" />
                            <div class="text-h6 font-weight-bold mb-2">Controlla la tua email</div>
                            <p class="text-body-2 text-medium-emphasis mb-4">
                                Abbiamo inviato un link di conferma a <strong>{{ form.email }}</strong>.<br>
                                Clicca sul link per verificare il tuo indirizzo email e completare la registrazione.
                            </p>
                            <v-alert type="info" variant="tonal" density="compact" class="mb-4 text-left">
                                Dopo la conferma email, il nostro team verificherà la tua richiesta e attiverà
                                l'account. Riceverai una notifica all'indirizzo indicato.
                            </v-alert>
                            <v-btn variant="tonal" color="primary" :to="{ path: '/login' }">
                                Torna al login
                            </v-btn>
                        </v-card-text>
                    </v-card>
                </template>

            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { ref, reactive } from 'vue'
import { registra_azienda } from '@/api/api.registrazione'

export default {
    name: 'RegistrazioneAzienda',

    setup() {
        const step = ref(1)
        const loading = ref(false)
        const showPass = ref(false)
        const formRef = ref(null)

        const form = reactive({
            ragioneSociale: '',
            pIva: '',
            email: '',
            password: '',
            confermaPassword: ''
        })

        const rules = {
            required: v => !!v || 'Campo obbligatorio',
            email: v => /.+@.+\..+/.test(v) || 'Email non valida',
            pIva: v => /^\d{11}$/.test(v) || 'Partita IVA non valida (11 cifre)',
            password: v => {
                if (!v || v.length < 8) return 'Minimo 8 caratteri'
                if (!/[A-Z]/.test(v)) return 'Serve almeno una maiuscola'
                if (!/[^a-zA-Z0-9]/.test(v)) return 'Serve almeno un carattere speciale'
                return true
            },
            confermaPassword: v => v === form.password || 'Le password non coincidono'
        }

        const invia = async () => {
            const { valid } = await formRef.value.validate()
            if (!valid) return

            loading.value = true
            try {
                await registra_azienda({
                    ragioneSociale: form.ragioneSociale,
                    pIva: form.pIva,
                    email: form.email,
                    password: form.password,
                    confermaPassword: form.confermaPassword
                })
                step.value = 2
            } catch {
                // Errori gestiti dall'interceptor (snackbar globale)
            } finally {
                loading.value = false
            }
        }

        return { step, loading, showPass, formRef, form, rules, invia }
    }
}
</script>

<style scoped>
.registrazione-container {
    background: linear-gradient(135deg, #1E3A5F 0%, #14273F 100%);
    min-height: 100vh;
}

.reg-card {
    border-radius: 12px;
}

.section-label {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: .5px;
    display: flex;
    align-items: center;
}


@media (max-width: 600px) {
    .reg-card {
        border-radius: 0;
    }
}
</style>